import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { cn, toAscii } from '@/lib/utils';
import { toast } from 'sonner';
import { fetchDevices, type VMOSDevice } from '@/services/vmos-api';
import { listBrands } from '@/services/brands-api';
import { listSlackBots, type SlackBot } from '@/services/slack-bots-api';
import { uploadFiles } from '@/services/api';
import {
  createScheduledRun,
  updateScheduledRun,
  deleteScheduledRun,
  type ScheduledRun,
  type CreateScheduleRequest,
  type UpdateScheduleRequest,
} from '@/services/scheduled-runs-api';
import {
  SavedPromptsPanel,
  type RunFormSnapshot,
} from '@/components/prompts/SavedPromptsPanel';
import { useTemplate as recordTemplateUse } from '@/services/prompts-api';
import type { Persona } from '@/services/prompts-api';
import type { PromptTemplate } from '@/services/prompts-api';
import { Loader2, Search, Trash2, Upload } from 'lucide-react';

const DEFAULT_LLM_MODEL = 'gemini-3-flash-preview';
const DEFAULT_COMMENT_MODEL = 'gemini-3-flash-preview';

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Manila',
  'Australia/Sydney',
  'Pacific/Auckland',
] as const;

type RunType = 'agentic_group' | 'content_upload';
type TimingMode = 'specific' | 'random';
type ScheduleType = 'once' | 'daily';
type ContentPlatform = 'tiktok' | 'facebook' | 'instagram' | 'youtube';
type YouTubeFormat = 'shorts' | 'video';

const browserTz = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
})();

interface ScheduleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedule?: ScheduledRun | null;
  defaultDate?: Date;
  onSaved: () => void;
  readOnly?: boolean;
}

export function ScheduleSheet({
  open,
  onOpenChange,
  schedule,
  defaultDate,
  onSaved,
  readOnly = false,
}: ScheduleSheetProps) {
  const isEdit = !!schedule;
  const lastRunStatus = schedule?.last_run_status;
  const lastRunLabel = lastRunStatus === 'failed'
    ? 'failed'
    : lastRunStatus === 'completed_with_issues'
      ? 'completed with issues'
      : lastRunStatus === 'stopped'
        ? 'stopped'
        : null;
  const lastRunTone = lastRunStatus === 'failed'
    ? 'border-destructive/40 bg-destructive/5 text-destructive'
    : lastRunStatus === 'completed_with_issues'
      ? 'border-amber-500/40 bg-amber-500/5 text-amber-700'
      : 'border-border bg-muted/40 text-muted-foreground';
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const savingRef = useRef(false);
  const deletingRef = useRef(false);

  const [runType, setRunType] = useState<RunType>(schedule?.run_type ?? 'agentic_group');
  const [name, setName] = useState(schedule?.name ?? '');
  const [selectedDevices, setSelectedDevices] = useState<string[]>(
    schedule?.config?.pad_ids ?? []
  );
  const [timezone, setTimezone] = useState(
    schedule?.timezone ?? (browserTz && TIMEZONES.includes(browserTz as any) ? browserTz : 'UTC')
  );
  const [timingMode, setTimingMode] = useState<TimingMode>(schedule?.timing_mode ?? 'specific');
  const [scheduleType, setScheduleType] = useState<ScheduleType>(schedule?.schedule_type ?? 'once');

  function getLocalDateTime(utcIso: string, tz: string) {
    const d = new Date(utcIso);
    return {
      date: new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d),
      time: new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(d),
    };
  }

  function defaultInitTime(): string {
    const now = new Date();
    const mins = now.getMinutes();
    const rounded = Math.ceil(mins / 15) * 15;
    const d = new Date(now);
    d.setMinutes(rounded, 0, 0);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  const minDate = (() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  })();

  const initDt = schedule?.scheduled_at
    ? getLocalDateTime(schedule.scheduled_at, schedule.timezone || browserTz || 'UTC')
    : { date: defaultDate?.toISOString().split('T')[0] ?? minDate, time: defaultInitTime() };
  const [scheduledDate, setScheduledDate] = useState(initDt.date);
  const [scheduledTime, setScheduledTime] = useState(initDt.time);
  const [randomStart, setRandomStart] = useState(schedule?.random_window_start ?? '09:00');
  const [randomEnd, setRandomEnd] = useState(schedule?.random_window_end ?? '17:00');
  const [maxRunsPerDay, setMaxRunsPerDay] = useState(schedule?.max_runs_per_day ?? 1);

  // Agentic group fields
  const [task, setTask] = useState(schedule?.config?.task ?? '');
  const [persona, setPersona] = useState(schedule?.config?.persona ?? '');
  const [brandId, setBrandId] = useState(schedule?.config?.brand_id || '_none');
  const [maxSteps, setMaxSteps] = useState(schedule?.config?.max_steps ?? 200);
  const [disableProactiveSearch, setDisableProactiveSearch] = useState(
    schedule?.config?.disable_proactive_search ?? false
  );
  const [captureRequired, setCaptureRequired] = useState(
    schedule?.config?.capture_required ?? true
  );
  const [maxCommentsPerSession, setMaxCommentsPerSession] = useState<number | ''>(
    schedule?.config?.max_comments_per_session ?? ''
  );
  const [maxSearchesPerSession, setMaxSearchesPerSession] = useState<number | ''>(
    schedule?.config?.max_searches_per_session ?? ''
  );
  const [appliedTemplateId, setAppliedTemplateId] = useState<string | null>(null);
  const [agenticEngagement, setAgenticEngagement] = useState(schedule?.config?.engagement ?? '');
  const [agenticPlatform, setAgenticPlatform] = useState(schedule?.config?.platform ?? '');

  // Content upload fields
  const [homeOnFinish, setHomeOnFinish] = useState(
    schedule?.config?.return_home_on_finish ?? true
  );

  const [contentType, setContentType] = useState<'video' | 'slideshow'>(
    schedule?.config?.content_type ?? 'video'
  );
  const [caption, setCaption] = useState(schedule?.config?.caption ?? '');
  const [soundUrl, setSoundUrl] = useState(schedule?.config?.sound_url ?? '');
  const [youtubeFormat, setYoutubeFormat] = useState<YouTubeFormat | ''>(
    (schedule?.config?.youtube_format as YouTubeFormat) ?? ''
  );
  const [youtubeTitle, setYoutubeTitle] = useState(schedule?.config?.title ?? '');
  const [youtubeDescription, setYoutubeDescription] = useState(schedule?.config?.description ?? '');
  const [youtubeRegularConfirm, setYoutubeRegularConfirm] = useState(false);
  const [videoMeta, setVideoMeta] = useState<{ duration: number; width: number; height: number } | null>(null);
  const [perPlatformCaptions, setPerPlatformCaptions] = useState(
    Array.isArray(schedule?.config?.platform_groups) && (schedule?.config?.platform_groups ?? []).length > 1
  );
  const [platformCaptions, setPlatformCaptions] = useState<Partial<Record<ContentPlatform, string>>>(
    schedule?.config?.per_platform_captions ?? {}
  );
  const [platformSounds, setPlatformSounds] = useState<Partial<Record<ContentPlatform, string>>>(
    schedule?.config?.per_platform_sounds ?? {}
  );
  const [platformsByDevice, setPlatformsByDevice] = useState<Record<string, ContentPlatform[]>>(
    () => {
      const groups = schedule?.config?.platform_groups;
      if (Array.isArray(groups) && groups.length > 0) {
        const map: Record<string, ContentPlatform[]> = {};
        for (const g of groups as Array<{ platform: string; pad_ids: string[] }>) {
          const p = (g.platform || 'tiktok') as ContentPlatform;
          for (const padId of g.pad_ids ?? []) {
            map[padId] = [...(map[padId] ?? []), p];
          }
        }
        return map;
      }
      // Legacy single-platform config: give every selected device that platform.
      const single = (schedule?.config?.platform ?? 'tiktok') as ContentPlatform;
      const map: Record<string, ContentPlatform[]> = {};
      for (const padId of schedule?.config?.pad_ids ?? []) {
        map[padId] = [single];
      }
      return map;
    }
  );
  const [files, setFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Read duration/dimensions so YouTube Shorts can be validated before saving.
  useEffect(() => {
    if (contentType !== 'video' || files.length === 0) {
      setVideoMeta(null);
      return;
    }
    const url = URL.createObjectURL(files[0]);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;
    const onLoaded = () => {
      setVideoMeta({
        duration: Number.isFinite(video.duration) ? video.duration : 0,
        width: video.videoWidth,
        height: video.videoHeight,
      });
    };
    video.addEventListener('loadedmetadata', onLoaded);
    return () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
    };
  }, [files, contentType]);

  const firstFileKey = files[0] ? `${files[0].name}:${files[0].size}` : '';
  useEffect(() => {
    setYoutubeRegularConfirm(false);
  }, [firstFileKey, youtubeFormat, contentType]);

  // Slack
  const [slackBotId, setSlackBotId] = useState(schedule?.slack_bot_id || '_none');

  const [deviceSearch, setDeviceSearch] = useState('');

  const { data: devicesData } = useQuery({
    queryKey: ['vmos-devices'],
    queryFn: fetchDevices,
  });
  const devices = useMemo(() => devicesData?.devices ?? [], [devicesData]);

  const { data: brands } = useQuery({
    queryKey: ['brands'],
    queryFn: listBrands,
  });

  const { data: slackBots } = useQuery({
    queryKey: ['slack-bots'],
    queryFn: () => listSlackBots(),
  });

  const filteredDevices = useMemo(() => {
    const q = deviceSearch.toLowerCase();
    if (!q) return devices;
    return devices.filter(
      (d) =>
        (d.device_name?.toLowerCase() || '').includes(q) ||
        (d.pad_id?.toLowerCase() || '').includes(q) ||
        (d.platform?.toLowerCase() || '').includes(q)
    );
  }, [devices, deviceSearch]);

  const toggleDevice = useCallback((padId: string) => {
    if (runType === 'content_upload') {
      // Content mode: device is "on" when it has at least one platform.
      setPlatformsByDevice((prev) => {
        const cur = prev[padId] ?? [];
        if (cur.length > 0) {
          const next = { ...prev };
          delete next[padId];
          return next;
        }
        if (Object.keys(prev).length >= 5) return prev;
        // Default to the device's stored platform if it maps cleanly, else
        // TikTok (the stored value is only a hint since it's unreliable).
        const stored = String(devices.find(d => d.pad_id === padId)?.platform || '').toLowerCase();
        let platform: ContentPlatform = 'tiktok';
        if (stored.includes('facebook') || stored.includes('fb')) platform = 'facebook';
        else if (stored.includes('instagram') || stored.includes('ig')) platform = 'instagram';
        else if (stored.includes('youtube') || stored === 'yt') platform = 'youtube';
        return { ...prev, [padId]: [platform] };
      });
      return;
    }
    setSelectedDevices((prev) =>
      prev.includes(padId)
        ? prev.filter((id) => id !== padId)
        : prev.length < 5
          ? [...prev, padId]
          : prev
    );
  }, [runType, devices]);

  const toggleDevicePlatform = useCallback((padId: string, platform: ContentPlatform) => {
    setPlatformsByDevice((prev) => {
      const cur = prev[padId] ?? [];
      const next = { ...prev };
      if (cur.includes(platform)) {
        const filtered = cur.filter(p => p !== platform);
        if (filtered.length === 0) delete next[padId];
        else next[padId] = filtered;
      } else {
        next[padId] = [...cur, platform];
      }
      if (contentType === 'slideshow') {
        const all = Object.values(next).flat();
        if (all.some(p => p !== 'tiktok' && p !== 'instagram' && p !== 'facebook')) {
          setContentType('video');
          setFiles([]);
        }
      }
      return next;
    });
  }, [contentType]);

  const activePlatforms = useMemo(() => {
    const set = new Set<ContentPlatform>();
    for (const arr of Object.values(platformsByDevice)) for (const p of arr) set.add(p);
    return (['tiktok', 'facebook', 'instagram', 'youtube'] as ContentPlatform[]).filter(p => set.has(p));
  }, [platformsByDevice]);

  const canUseSlideshow =
    activePlatforms.length === 0 ||
    activePlatforms.every(p => p === 'tiktok' || p === 'instagram' || p === 'facebook');
  const multiPhotoLabel = activePlatforms.length === 0
    ? 'Slideshow / Carousel'
    : activePlatforms.every(p => p === 'tiktok')
      ? 'Slideshow'
      : activePlatforms.includes('tiktok')
        ? 'Slideshow / Carousel'
        : 'Carousel';

  const isYouTubeActive = activePlatforms.includes('youtube');
  const nonYoutubePlatforms = activePlatforms.filter(p => p !== 'youtube');
  const youtubeVideoIsVertical = !!videoMeta && videoMeta.height > videoMeta.width;
  const youtubeVideoIsShortLength = !!videoMeta && videoMeta.duration > 0 && videoMeta.duration <= 180;
  const youtubeShortsInvalid =
    isYouTubeActive &&
    youtubeFormat === 'shorts' &&
    !!videoMeta &&
    (!youtubeVideoIsVertical || !youtubeVideoIsShortLength);
  const youtubeRegularNeedsConfirm =
    isYouTubeActive &&
    youtubeFormat === 'video' &&
    !!videoMeta &&
    youtubeVideoIsVertical &&
    youtubeVideoIsShortLength;

  // Content runs keep selectedDevices in sync = every pad with a platform.
  useEffect(() => {
    if (runType !== 'content_upload') return;
    setSelectedDevices(Object.keys(platformsByDevice));
  }, [platformsByDevice, runType]);

  const handleApplyTemplate = useCallback((t: PromptTemplate) => {
    setTask(t.task);
    setPersona(t.persona_text ?? '');
    setBrandId(t.brand_id ?? '_none');
    if (typeof t.max_steps === 'number') setMaxSteps(t.max_steps);
    setDisableProactiveSearch(!!t.disable_proactive_search);
    setCaptureRequired(t.capture_required ?? true);
    setMaxCommentsPerSession(t.max_comments_per_session ?? '');
    setMaxSearchesPerSession(t.max_searches_per_session ?? '');
  }, []);

  const handleApplyPersona = useCallback((p: Persona) => {
    setPersona(p.persona_text);
  }, []);

  const runFormValues: RunFormSnapshot = {
    task,
    persona,
    brandId,
    llmModel: DEFAULT_LLM_MODEL,
    commentLlmModel: DEFAULT_COMMENT_MODEL,
    maxSteps,
    disableProactiveSearch,
    captureRequired,
    returnHomeOnFinish: true,
    maxCommentsPerSession,
    maxSearchesPerSession,
  };

  const handleFileSelect = useCallback((newFiles: File[]) => {
    if (runType === 'content_upload') {
      setFiles(newFiles);
    }
  }, [runType]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(Array.from(e.dataTransfer.files));
    }
  }, [handleFileSelect]);

  const handleSave = async () => {
    if (savingRef.current) return;
    if (readOnly) return;

    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (selectedDevices.length === 0) {
      toast.error('Select at least one device');
      return;
    }
    if (runType === 'content_upload' && activePlatforms.includes('youtube')) {
      if (!youtubeFormat) {
        toast.error('Select YouTube Shorts or regular video');
        return;
      }
      if (!youtubeTitle.trim()) {
        toast.error('YouTube title is required');
        return;
      }
      if (youtubeTitle.trim().length > 100) {
        toast.error('YouTube title must be 100 characters or fewer');
        return;
      }
      if (youtubeDescription.length > 5000) {
        toast.error('YouTube description must be 5000 characters or fewer');
        return;
      }
      const vertical = !!videoMeta && videoMeta.height > videoMeta.width;
      const shortLength = !!videoMeta && videoMeta.duration > 0 && videoMeta.duration <= 180;
      if (youtubeFormat === 'shorts' && videoMeta && (!vertical || !shortLength)) {
        toast.error('Shorts require a vertical video that is 3 minutes or less');
        return;
      }
      if (youtubeFormat === 'video' && videoMeta && vertical && shortLength && !youtubeRegularConfirm) {
        toast.error('Confirm this vertical video should be published as a regular video');
        return;
      }
    }

    const isSkipped = schedule?.status === 'skipped';
    if (isSkipped && timingMode === 'specific') {
      const scheduledLocal = new Date(`${scheduledDate}T${scheduledTime}:00`);
      const minTime = new Date(Date.now() + 5 * 60 * 1000);
      if (scheduledLocal < minTime) {
        toast.error('Scheduled time must be at least 5 minutes from now when re-activating a skipped schedule');
        return;
      }
    }

    savingRef.current = true;
    setSaving(true);
    try {
      const config: Record<string, any> = { pad_ids: selectedDevices };

      if (runType === 'agentic_group') {
        config.task = toAscii(task.trim());
        config.persona = toAscii(persona.trim());
        if (agenticEngagement) {
          config.engagement = agenticEngagement;
          if (agenticPlatform) config.platform = agenticPlatform;
        }
        config.brand_id = brandId !== '_none' ? brandId : undefined;
        config.llm_model = DEFAULT_LLM_MODEL;
        config.comment_llm_model = DEFAULT_COMMENT_MODEL;
        config.max_steps = maxSteps;
        config.disable_proactive_search = disableProactiveSearch;
        config.capture_required = captureRequired;
        if (maxCommentsPerSession !== '') {
          config.max_comments_per_session = maxCommentsPerSession;
        }
        if (maxSearchesPerSession !== '') {
          config.max_searches_per_session = maxSearchesPerSession;
        }
      } else {
        // Legacy single-platform config fields kept for back-compat; the source
        // of truth for platforms is platform_groups below.
        const primaryPlatform = activePlatforms[0] ?? 'tiktok';
        const hasYouTube = activePlatforms.includes('youtube');
        config.platform = primaryPlatform;
        config.content_type = contentType;
        config.caption = caption;
        if (primaryPlatform === 'tiktok' && soundUrl.trim()) {
          config.sound_url = soundUrl.trim();
        }
        if (hasYouTube) {
          config.title = youtubeTitle.trim();
          config.description = youtubeDescription.trim();
          config.youtube_format = youtubeFormat;
        }
        config.return_home_on_finish = homeOnFinish;
        config.llm_model = DEFAULT_LLM_MODEL;
        config.comment_llm_model = DEFAULT_COMMENT_MODEL;
        config.max_steps = maxSteps;

        // Per-device platform assignments -> ordered platform_groups.
        const groups = activePlatforms.map((platform) => {
          const padIds = Object.entries(platformsByDevice)
            .filter(([, arr]) => arr.includes(platform))
            .map(([padId]) => padId);
          const groupCaption = perPlatformCaptions && (platformCaptions[platform] ?? '').trim()
            ? (platformCaptions[platform] ?? '').trim()
            : caption.trim();
          const groupSound = perPlatformCaptions && (platformSounds[platform] ?? '').trim()
            ? (platformSounds[platform] ?? '').trim()
            : (platform === 'tiktok' ? soundUrl.trim() : '');
          if (platform === 'youtube') {
            return {
              platform,
              pad_ids: padIds,
              title: youtubeTitle.trim(),
              description: youtubeDescription.trim(),
              youtube_format: youtubeFormat,
            };
          }
          return {
            platform,
            pad_ids: padIds,
            caption: groupCaption,
            sound_url: platform === 'tiktok' ? groupSound : '',
          };
        });
        if (groups.length > 1) {
          config.platform_groups = groups;
          config.per_platform_captions = perPlatformCaptions ? platformCaptions : undefined;
          config.per_platform_sounds = perPlatformCaptions ? platformSounds : undefined;
        }
        config.pad_ids = Object.keys(platformsByDevice);

        // Upload files to R2 before saving the schedule
        if (files.length > 0) {
          const result = await uploadFiles(files);
          config.files = result.files.map((f) => ({
            uri: f.uri,
            original_name: f.original_name,
            size: f.size,
          }));
        } else if (isEdit && schedule?.config?.files) {
          config.files = schedule.config.files;
        } else {
          config.files = [];
        }
      }

      let scheduledAt: string | undefined;
      if (timingMode === 'specific') {
        scheduledAt = `${scheduledDate}T${scheduledTime}:00`;
      }

      if (isEdit && schedule) {
        const body: UpdateScheduleRequest = {
          name,
          timing_mode: timingMode,
          schedule_type: scheduleType,
          timezone,
          ...(timingMode === 'specific' ? { scheduled_at: scheduledAt } : {}),
          ...(timingMode === 'random' ? {
            random_window_start: randomStart,
            random_window_end: randomEnd,
            max_runs_per_day: maxRunsPerDay,
          } : {}),
          config,
          slack_bot_id: slackBotId !== '_none' ? slackBotId : (
            schedule?.slack_bot_id ? "" : undefined
          ),
          ...(isSkipped ? { status: 'active' } : {}),
        };
        await updateScheduledRun(schedule.id, body);
        toast.success(isSkipped ? 'Schedule re-activated' : 'Schedule updated');
      } else {
        // Create schedule first (without files) to avoid orphaned R2 objects
        const createConfig = { ...config };
        const hasFiles = runType === 'content_upload' && files.length > 0;
        if (hasFiles) {
          createConfig.files = [];
        }
        const body: CreateScheduleRequest = {
          name,
          run_type: runType,
          timing_mode: timingMode,
          schedule_type: scheduleType,
          timezone,
          ...(timingMode === 'specific' ? { scheduled_at: scheduledAt } : {}),
          ...(timingMode === 'random' ? {
            random_window_start: randomStart,
            random_window_end: randomEnd,
            max_runs_per_day: maxRunsPerDay,
          } : { max_runs_per_day: 1 }),
          config: createConfig,
          slack_bot_id: slackBotId !== '_none' ? slackBotId : undefined,
        };
        const created = await createScheduledRun(body);

        // Upload files after successful schedule creation
        if (hasFiles) {
          const result = await uploadFiles(files);
          const fileRefs = result.files.map((f) => ({
            uri: f.uri,
            original_name: f.original_name,
            size: f.size,
          }));
          await updateScheduledRun(created.id, { config: { ...createConfig, files: fileRefs } });
        }
        toast.success('Schedule created');
      }

      // Attribute this schedule to the saved prompt it came from (fire-and-forget).
      if (runType === 'agentic_group' && appliedTemplateId) {
        recordTemplateUse(appliedTemplateId).catch(() => {});
      }

      onSaved();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || 'Failed to save schedule');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deletingRef.current) return;
    if (!schedule) return;
    deletingRef.current = true;
    setDeleting(true);
    try {
      await deleteScheduledRun(schedule.id);
      toast.success('Schedule deleted');
      onSaved();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || 'Failed to delete');
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[480px] sm:max-w-[480px] flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-2">
          <SheetTitle className="text-lg">{isEdit ? 'Edit Schedule' : 'New Schedule'}</SheetTitle>
          <SheetDescription>
            {isEdit ? 'Modify the schedule details.' : 'Schedule an agent run or content post.'}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6">
          <div className="space-y-8 pb-6">
          {isEdit && lastRunLabel && (
            <div className={`rounded border p-2 text-xs ${lastRunTone}`}>
              <div className="font-medium">Last run: {lastRunLabel}</div>
              {schedule?.last_run_error && (
                <div className="mt-0.5 opacity-80 break-words">{schedule.last_run_error}</div>
              )}
            </div>
          )}
          {/* Type */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Type</Label>
            <Tabs value={runType} onValueChange={(v) => setRunType(v as RunType)}>
              <TabsList className="w-full">
                <TabsTrigger
                  value="agentic_group"
                  className="flex-1 data-[state=active]:bg-amber-500 data-[state=active]:text-white data-[state=active]:font-semibold"
                  disabled={readOnly}
                >
                  Agent Run
                </TabsTrigger>
                <TabsTrigger
                  value="content_upload"
                  className="flex-1 data-[state=active]:bg-violet-500 data-[state=active]:text-white data-[state=active]:font-semibold"
                  disabled={readOnly}
                >
                  Content Post
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Name */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Morning TikTok run"
              disabled={readOnly}
            />
          </div>

          {/* Devices */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">
              Devices ({selectedDevices.length}/5)
              {runType === 'content_upload' && activePlatforms.length > 0 && (
                <span className="ml-2 font-mono text-[11px] text-primary">
                  {activePlatforms.map((p) => {
                    const count = Object.values(platformsByDevice).filter(arr => arr.includes(p)).length;
                    return `${p[0].toUpperCase() + p.slice(1)} ×${count}`;
                  }).join(' · ')}
                </span>
              )}
            </Label>
            <div className="relative mb-2">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search devices..."
                value={deviceSearch}
                onChange={(e) => setDeviceSearch(e.target.value)}
                className="pl-8 h-8 text-xs"
                disabled={readOnly}
              />
            </div>
            <div className="space-y-1 max-h-40 overflow-y-auto border rounded-md p-2">
              {filteredDevices.map((d: VMOSDevice) => {
                const selected = runType === 'content_upload'
                  ? (platformsByDevice[d.pad_id] ?? []).length > 0
                  : selectedDevices.includes(d.pad_id);
                return (
                  <div key={d.pad_id} className={cn(selected && 'bg-primary/5 rounded')}>
                    <label
                      className={cn(
                        'flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-muted/50'
                      )}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => toggleDevice(d.pad_id)}
                        disabled={
                          readOnly ||
                          (runType !== 'content_upload' &&
                            !selectedDevices.includes(d.pad_id) &&
                            selectedDevices.length >= 5)
                        }
                      />
                      <span className="flex-1">
                        {d.device_name || d.pad_id?.slice(0, 8)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {d.platform || 'Device'}
                      </span>
                    </label>
                    {runType === 'content_upload' && selected && (
                      <div className="flex items-center gap-1.5 px-2 pb-2 pl-8 flex-wrap">
                        {(['tiktok', 'facebook', 'instagram', 'youtube'] as ContentPlatform[]).map((platform) => {
                          const on = (platformsByDevice[d.pad_id] ?? []).includes(platform);
                          return (
                            <button
                              type="button"
                              key={platform}
                              onClick={() => toggleDevicePlatform(d.pad_id, platform)}
                              className={cn(
                                'text-[11px] font-medium px-2 py-0.5 rounded border transition-colors',
                                on
                                  ? 'bg-primary text-primary-foreground border-primary'
                                  : 'text-muted-foreground border-input hover:border-primary/50'
                              )}
                            >
                              {platform[0].toUpperCase() + platform.slice(1)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredDevices.length === 0 && (
                <p className="text-xs text-muted-foreground p-2">
                  {deviceSearch ? 'No devices match your search' : 'No devices available'}
                </p>
              )}
            </div>
            {runType === 'content_upload' && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Pick device(s) and toggle the platform(s) each posts to. Multiple platforms run one at a time in order.
              </p>
            )}
          </div>

          {/* Timezone */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Timezone</Label>
            <Select value={timezone} onValueChange={setTimezone} disabled={readOnly}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Timing Mode */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Timing</Label>
            <Tabs
              value={timingMode}
              onValueChange={(v) => setTimingMode(v as TimingMode)}
            >
              <TabsList className="w-full">
                <TabsTrigger value="specific" className="flex-1" disabled={readOnly}>
                  Specific Time
                </TabsTrigger>
                <TabsTrigger value="random" className="flex-1" disabled={readOnly}>
                  Random Window
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="mt-3">
              {timingMode === 'specific' ? (
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    className="flex-1"
                    disabled={readOnly}
                    min={minDate}
                  />
                  <Input
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-32"
                    disabled={readOnly}
                    min={scheduledDate === minDate ? defaultInitTime() : undefined}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-muted-foreground w-12">Start</span>
                    <Input
                      type="time"
                      value={randomStart}
                      onChange={(e) => setRandomStart(e.target.value)}
                      className="flex-1"
                      disabled={readOnly}
                    />
                    <span className="text-xs text-muted-foreground w-8">End</span>
                    <Input
                      type="time"
                      value={randomEnd}
                      onChange={(e) => setRandomEnd(e.target.value)}
                      className="flex-1"
                      disabled={readOnly}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Max runs/day</span>
                    <Input
                      type="number"
                      min={1}
                      max={5}
                      value={maxRunsPerDay}
                      onChange={(e) => setMaxRunsPerDay(Number(e.target.value))}
                      className="w-20"
                      disabled={readOnly}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Repeat */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">Repeat</Label>
            <Tabs
              value={scheduleType}
              onValueChange={(v) => setScheduleType(v as ScheduleType)}
            >
              <TabsList className="w-full">
                <TabsTrigger value="once" className="flex-1" disabled={readOnly}>
                  Once
                </TabsTrigger>
                <TabsTrigger value="daily" className="flex-1" disabled={readOnly}>
                  Daily
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {/* Configuration (conditional on run_type) */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">
              Configuration
            </Label>

            {runType === 'agentic_group' ? (
              <div className="space-y-3">
                <SavedPromptsPanel
                  values={runFormValues}
                  onApplyTemplate={handleApplyTemplate}
                  onApplyPersona={handleApplyPersona}
                  onAppliedTemplateChange={setAppliedTemplateId}
                  disabled={readOnly}
                  selectedPadIds={selectedDevices}
                />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Engagement (device prompts)</Label>
                    <Select value={agenticEngagement || 'off'} onValueChange={(v) => setAgenticEngagement(v === 'off' ? '' : v)} disabled={readOnly}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="off">Off — use the task</SelectItem>
                        <SelectItem value="full">Full engagement</SelectItem>
                        <SelectItem value="lurk">Lurk</SelectItem>
                        <SelectItem value="recovery">Recovery</SelectItem>
                        <SelectItem value="recovery_exploration">Recovery exploration</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Platform</Label>
                    <Select value={agenticPlatform || 'auto'} onValueChange={(v) => setAgenticPlatform(v === 'auto' ? '' : v)} disabled={readOnly}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto (per device)</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="youtube">YouTube Shorts</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {agenticEngagement && (
                  <p className="text-[11px] text-muted-foreground">
                    Each scheduled device runs its own saved prompt/persona; the Task below acts as an override.
                  </p>
                )}
                <div>
                  <Label className="text-xs">Task</Label>
                  <Textarea
                    value={task}
                    onChange={(e) => setTask(e.target.value)}
                    placeholder="Like 5 videos and follow 2 creators..."
                    rows={3}
                    disabled={readOnly}
                  />
                </div>
                <div>
                  <Label className="text-xs">Persona</Label>
                  <Textarea
                    value={persona}
                    onChange={(e) => setPersona(e.target.value)}
                    placeholder="You are a social media manager..."
                    rows={2}
                    disabled={readOnly}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={disableProactiveSearch}
                      onCheckedChange={setDisableProactiveSearch}
                      disabled={readOnly}
                    />
                    <Label className="text-xs">Feed only (no proactive search)</Label>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <Switch
                      checked={captureRequired}
                      onCheckedChange={setCaptureRequired}
                      disabled={readOnly}
                    />
                    <Label className="text-xs">Require post-URL capture for FB posts / Reddit</Label>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Brand</Label>
                  <Select value={brandId} onValueChange={setBrandId} disabled={readOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">None</SelectItem>
                      {brands?.map((b: any) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">
                    Max Steps: {maxSteps}
                  </Label>
                  <Slider
                    value={[maxSteps]}
                    onValueChange={([v]) => setMaxSteps(v)}
                    min={180}
                    max={500}
                    step={10}
                    disabled={readOnly}
                    className={readOnly ? 'pointer-events-none opacity-50' : undefined}
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label className="text-xs">Max Comments / Session</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="Auto (8)"
                      value={maxCommentsPerSession}
                      onChange={(e) =>
                        setMaxCommentsPerSession(
                          e.target.value === '' ? '' : Math.max(0, Number(e.target.value))
                        )
                      }
                      disabled={readOnly}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Blank = default (8). Set 0 for warmup / lurk sessions.
                    </p>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Max Searches / Session</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="Auto (5)"
                      value={maxSearchesPerSession}
                      onChange={(e) =>
                        setMaxSearchesPerSession(
                          e.target.value === '' ? '' : Math.max(0, Number(e.target.value))
                        )
                      }
                      disabled={readOnly}
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Blank = default (5). Set 0 for warmup / lurk sessions.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-[11px] text-muted-foreground">
                  Platforms are chosen per device above (a device can post to multiple).
                  When more than one platform is selected, content posts one platform at a time, in order.
                </p>
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Type</Label>
                  <Tabs
                    value={contentType}
                    onValueChange={(v) => {
                      setContentType(v as 'video' | 'slideshow');
                      setFiles([]);
                    }}
                  >
                    <TabsList className="w-full">
                      <TabsTrigger value="video" className="flex-1" disabled={readOnly}>Video</TabsTrigger>
                      {canUseSlideshow && (
                        <TabsTrigger value="slideshow" className="flex-1" disabled={readOnly}>{multiPhotoLabel}</TabsTrigger>
                      )}
                    </TabsList>
                  </Tabs>
                </div>
                <div>
                  <Label className="text-xs">Files</Label>
                  <div
                    className={cn(
                      'relative mt-1 border-2 border-dashed rounded-lg p-4 text-center transition-colors',
                      readOnly
                        ? 'opacity-50'
                        : 'cursor-pointer',
                      isDragging
                        ? 'border-lime-fill bg-lime-soft'
                        : 'border-input hover:border-lime-fill/50'
                    )}
                    onDragEnter={readOnly ? undefined : handleDragIn}
                    onDragLeave={readOnly ? undefined : handleDragOut}
                    onDragOver={readOnly ? undefined : handleDrag}
                    onDrop={readOnly ? undefined : handleDrop}
                    onClick={readOnly ? undefined : () => fileInputRef.current?.click()}
                  >
                    <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">
                      {files.length > 0
                        ? `${files.length} file(s) selected — drop to replace`
                        : isDragging
                          ? 'Drop files here'
                          : 'Click or drag files here'}
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={contentType === 'video' ? 'video/*' : 'image/*'}
                    multiple={contentType === 'slideshow'}
                    disabled={readOnly}
                    onChange={(e) => {
                      if (e.target.files?.length) {
                        setFiles(Array.from(e.target.files));
                      }
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                </div>
                {nonYoutubePlatforms.length > 1 && (
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={perPlatformCaptions}
                      onCheckedChange={setPerPlatformCaptions}
                      disabled={readOnly}
                    />
                    <Label className="text-xs">Different captions per platform</Label>
                  </div>
                )}
                {nonYoutubePlatforms.length > 0 && (
                  perPlatformCaptions && nonYoutubePlatforms.length > 1 ? (
                    nonYoutubePlatforms.map((platform) => (
                      <div key={platform} className="space-y-1.5 border rounded-lg p-2">
                        <Label className="text-xs capitalize">{platform} caption</Label>
                        <Textarea
                          value={platformCaptions[platform] ?? ''}
                          onChange={(e) => setPlatformCaptions((prev) => ({ ...prev, [platform]: e.target.value }))}
                          placeholder={`Caption for ${platform}...`}
                          rows={2}
                          disabled={readOnly}
                        />
                        {platform === 'tiktok' && (
                          <Input
                            type="url"
                            value={platformSounds.tiktok ?? ''}
                            onChange={(e) => setPlatformSounds((prev) => ({ ...prev, tiktok: e.target.value }))}
                            placeholder="TikTok sound URL (optional)"
                            disabled={readOnly}
                          />
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs">Caption</Label>
                        <Textarea
                          value={caption}
                          onChange={(e) => setCaption(e.target.value)}
                          placeholder="Enter your caption..."
                          rows={3}
                          disabled={readOnly}
                        />
                      </div>
                      {activePlatforms.includes('tiktok') && (
                        <div>
                          <Label className="text-xs">Sound URL (optional)</Label>
                          <Input
                            type="url"
                            value={soundUrl}
                            onChange={(e) => setSoundUrl(e.target.value)}
                            placeholder="Paste a TikTok video link to use its sound"
                            disabled={readOnly}
                          />
                        </div>
                      )}
                    </div>
                  )
                )}
                {isYouTubeActive && (
                  <div className="space-y-2 border rounded-lg p-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">YouTube</Label>
                      <span className="text-[11px] text-muted-foreground">Public · Not made for kids</span>
                    </div>
                    <div>
                      <Label className="text-xs">Format (required)</Label>
                      <Select
                        value={youtubeFormat || 'none'}
                        onValueChange={(v) => setYoutubeFormat(v === 'none' ? '' : (v as YouTubeFormat))}
                        disabled={readOnly}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Select Shorts or regular video</SelectItem>
                          <SelectItem value="shorts">Shorts (vertical, 3 min or less)</SelectItem>
                          <SelectItem value="video">Regular video</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Title (required)</Label>
                        <span className="text-[11px] text-muted-foreground">{youtubeTitle.trim().length}/100</span>
                      </div>
                      <Input
                        value={youtubeTitle}
                        onChange={(e) => setYoutubeTitle(e.target.value)}
                        maxLength={100}
                        placeholder="Short, catchy YouTube title"
                        disabled={readOnly}
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Description</Label>
                        <span className="text-[11px] text-muted-foreground">{youtubeDescription.length}/5000</span>
                      </div>
                      <Textarea
                        value={youtubeDescription}
                        onChange={(e) => setYoutubeDescription(e.target.value)}
                        maxLength={5000}
                        placeholder="YouTube description (optional)"
                        rows={2}
                        disabled={readOnly}
                      />
                    </div>
                    {youtubeShortsInvalid && (
                      <p className="text-xs text-destructive">
                        Shorts require a vertical video (taller than wide) that is 3 minutes or less.
                      </p>
                    )}
                    {youtubeRegularNeedsConfirm && (
                      <div className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/5 p-2">
                        <Checkbox
                          id="schedule-youtube-regular-confirm"
                          checked={youtubeRegularConfirm}
                          onCheckedChange={(v) => setYoutubeRegularConfirm(v === true)}
                          disabled={readOnly}
                        />
                        <label htmlFor="schedule-youtube-regular-confirm" className="text-xs text-amber-700 leading-snug cursor-pointer">
                          This video is vertical and under 3 minutes. YouTube may publish it as a Short. I confirm this should be a regular video and understand the agent will report a mismatch if it lands as a Short.
                        </label>
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">When done</Label>
                  <Select value={homeOnFinish ? 'home' : 'stay'} onValueChange={(v) => setHomeOnFinish(v === 'home')} disabled={readOnly}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="home">Return device to home screen</SelectItem>
                      <SelectItem value="stay">Leave app open (stay where agent left off)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          {/* Slack */}
          <div>
            <Label className="text-xs text-muted-foreground mb-2 block">
              Slack Notification
            </Label>
            <Select value={slackBotId} onValueChange={setSlackBotId} disabled={readOnly}>
              <SelectTrigger>
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {slackBots?.map((b: SlackBot) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          </div>
        </div>

        <SheetFooter>
          {!readOnly && (
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={savingRef.current || saving}
              className="flex-1"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEdit ? 'Save Changes' : 'Create Schedule'}
            </Button>
            {isEdit && (
              <Button
                variant="destructive"
                size="icon"
                onClick={handleDelete}
                disabled={deletingRef.current || deleting}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
